/**
 * SSE 소비자 — fetch + ReadableStream 으로 chatbot streaming 엔드포인트를 수신.
 * (axios 는 chunk 단위 수신 불가, EventSource 는 Authorization 헤더 불가 → fetch 필수.)
 *
 * 백엔드 이벤트 contract (ChatbotController.streamMessage 와 일치):
 *   - tool_call    { name, args }
 *   - tool_result  { name, latency_ms, ok, error?, summary? }
 *   - token        { text }
 *   - done         { responseTime, botCreatedAt, totalRounds, meta }
 *   - error        { message }
 */

import { axiosInstance } from '@/worker/commands/config/apiInstance';
import { i18n } from '@/common/locale';

/**
 * 선제 알람 RCA 진입 컨텍스트 (feature: chatbot-proactive-alarm).
 * 알람 카드 클릭 시 동봉 → 백엔드 formatAlarmContext 가 RCA 지시문으로 변환해 hiddenContext 에 병합.
 * 백엔드 ChatRequest.AlarmContext 와 1:1.
 */
export interface AlarmContext {
  alertRuleId?: string;
  targetId?: string;
  ruleName?: string;
  level?: string;
  triggerTime?: string;
  targetName?: string;
  targetKind?: string;
}

/**
 * 사용자가 입력창 '+' 버튼으로 명시 선택한 모니터링 타겟 (feature: chatbot-target-conversation).
 * 백엔드 ChatRequest.SelectedTarget 와 1:1. 발화 시 동봉되면 backend 가 USER-SELECTED TARGETS
 * 지시문으로 변환해 hiddenContext 에 병합 → LLM 이 이 타겟들로 도구/RCA scope.
 */
export interface SelectedTarget {
  targetId: string;
  name: string;
  kind: string;
  subKind?: string;
  alarmLevel?: string;
}

export interface StreamChatRequest {
  userId: string;
  /**
   * 이어가려는 기존 대화 ID (UUID). 미명시 시 backend 가 가장 최근 active 대화 사용,
   * 없으면 새 대화 자동 생성. SSE 응답의 'conversation' 이벤트로 실제 사용된 ID 통보.
   *
   * 기존 sessionToken (로그인 세션 단위 thread) 은 폐기 — 대화 단위 격리로 대체.
   */
  conversationId?: string;
  message: string;
  /** 사용자가 현재 보고 있는 페이지 컨텍스트 (선택). 백엔드 system prompt 에 inject 됨. */
  pageContext?: Record<string, unknown>;
  /**
   * "보이지 않는" 추가 prompt 컨텍스트 — 차트 분석 시 raw JSON 처럼 사용자/history 에는
   * 노출하지 않으면서 Gemini 에는 전달해야 하는 데이터. backend 가 마지막 user turn 에만
   * 임시로 append 하고 history 에는 저장하지 않는다.
   */
  hiddenContext?: string;
  /**
   * 선제 알람 RCA 진입점 (선택). 알람 카드 클릭 시 동봉 — 백엔드가 RCA 지시문으로 변환.
   * (feature: chatbot-proactive-alarm)
   */
  alarmContext?: AlarmContext;
  /**
   * 사용자가 '+' 버튼으로 선택한 타겟 (선택, 0..N). 비어있으면 미동봉(타겟 미지정 = 기존 동작).
   * (feature: chatbot-target-conversation)
   */
  targets?: SelectedTarget[];
}

export type StreamEventName =
  | 'tool_call'
  | 'tool_result'
  | 'navigate'
  | 'token'
  | 'status'
  | 'conversation'
  | 'replace'
  | 'done'
  | 'error';

export interface StreamHandlers {
  onToolCall?: (data: { name: string; args?: Record<string, unknown> }) => void;
  onToolResult?: (data: {
    name: string;
    latency_ms?: number;
    ok?: boolean;
    error?: string;
  }) => void;
  /**
   * navigate_to_screen / open_target_detail tool 결과로 발사. front 의 vue-router 가 좌측 main
   * 영역을 path 로 push. open_target_detail 의 경우 detail 블록이 포함되어 chat store 가
   * slide-detail 까지 자동으로 연다.
   */
  onNavigate?: (data: {
    path: string;
    label?: string;
    domain?: string;
    detail?: {
      type: string;
      target_id: string;
      target_name?: string;
      server?: string;
    };
    /** analyze_in_screen 이 보낸 화면 prefill — chat store 가 window 글로벌에 등록. */
    prefill?: {
      instance_id?: string;
      instance_name?: string;
      from_time?: string;
      to_time?: string;
      auto_search?: boolean;
      intent?: string;
      sub_kind?: string;
    };
  }) => void;
  onToken?: (data: { text: string }) => void;
  /** Lightweight overall 진행 indicator — chip 시스템(streamToolEvents)과 무관하게 항상 발사.
   *  kind='thinking' = LLM 라운드 진입 직전. kind='tool' = 도구 호출 직전 (name 필수). */
  onStatus?: (data: { kind: 'thinking' | 'tool'; name?: string }) => void;
  /**
   * stream 시작 시 1회 발사 — 백엔드가 실제 사용한 conversationId 통보.
   * isNew=true 면 새로 생성된 대화. front 의 useChatConversationsStore.activeId 갱신 + 목록 refetch.
   */
  onConversation?: (data: { conversationId: string; isNew: boolean }) => void;
  /**
   * 백엔드 후처리(수치 groundedness 정정 재생성)가 본문을 교체할 때 발사 —
   * 지금까지 스트리밍된 텍스트 전체를 payload.text 로 치환한다.
   */
  onReplace?: (data: { text: string; reason?: string }) => void;
  onDone?: (data: {
    responseTime?: string;
    botCreatedAt?: string;
    totalRounds?: number;
    /** 응답 메타 — {provider, model, inputTokens, outputTokens, totalTokens, elapsedMs}. */
    meta?: Record<string, unknown>;
    /** 인라인 차트 배열 (리포트 chart 섹션 스키마) — 추이/Top-N 도구 호출 시에만 존재. */
    charts?: Array<Record<string, unknown>>;
    /** 알람 임계치 추천 배열 — recommend_alarm_thresholds 호출 시에만 존재. */
    alarmRecommendations?: Array<Record<string, unknown>>;
    /** 구조화 응답 카드 — present_structured_answer 호출 시에만 존재(단일 객체). */
    structuredAnswer?: Record<string, unknown>;
  }) => void;
  onError?: (data: { message: string }) => void;
}

/**
 * Authorization 헤더는 axiosInstance.defaults 에 setHeaderConfig 로 설정되어 있음.
 * fetch 를 직접 쓰면 그 헤더가 자동 부착되지 않으므로 axios defaults 에서 가져와 명시 부착.
 */
function buildHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  // axios 의 Authorization 헤더 그대로 전달 (login 후 setHeaderConfig 로 주입됨).
  const auth =
    axiosInstance.defaults?.headers?.common?.Authorization ??
    axiosInstance.defaults?.headers?.Authorization;
  if (auth && typeof auth === 'string') {
    headers.Authorization = auth;
  }
  return headers;
}

/**
 * 한 번의 chatbot 메시지 전송 → SSE 이벤트 스트림 소비.
 * Promise 는 done 이벤트 (또는 stream 종료) 시 resolve.
 *
 * @param signal AbortController.signal — 사용자가 닫거나 새 메시지 보낼 때 중단.
 */
export async function streamChatbotMessage(
  request: StreamChatRequest,
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch('/api/v9/chatbot/message/stream', {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({
      userId: request.userId,
      conversationId: request.conversationId,
      message: request.message,
      messageType: 'text',
      pageContext: request.pageContext,
      hiddenContext: request.hiddenContext,
      alarmContext: request.alarmContext,
      targets: request.targets && request.targets.length ? request.targets : undefined,
    }),
    signal,
  });

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    handlers.onError?.({ message: `HTTP ${response.status} ${text || response.statusText}` });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  // 프록시/서버가 에러 프레임 없이 조용히 멈추면 read() 가 영원히 pending 되어 isLoading 이
  // 영구히 잠긴다 (입력창·리셋 버튼 비활성). 무신호 상한을 두고 reject 시켜 호출부(chat.ts)의
  // catch/finally 로 정리를 태운다. vLLM 등 느린 모델도 토큰/도구 이벤트는 이 간격 안에 온다.
  const READ_IDLE_TIMEOUT_MS = 180_000;
  const readNext = (): Promise<ReadableStreamReadResult<Uint8Array>> =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reader.cancel().catch(() => {});
        reject(
          new Error(
            i18n.global.t('WORD.CHATBOT.MESSAGE.STREAM_TIMEOUT', {
              sec: READ_IDLE_TIMEOUT_MS / 1000,
            }),
          ),
        );
      }, READ_IDLE_TIMEOUT_MS);
      reader.read().then(
        (result) => {
          clearTimeout(timer);
          resolve(result);
        },
        (err) => {
          clearTimeout(timer);
          reject(err);
        },
      );
    });

  // SSE frame: 빈 줄(\n\n) 단위. 각 frame 은 여러 'event:' / 'data:' 라인을 가짐.
  while (true) {
    const { value, done } = await readNext();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let frameEnd: number;
    // 두 줄바꿈으로 구분된 완성 frame 만 처리, 미완은 buffer 에 남겨둠.
    while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, frameEnd);
      buffer = buffer.slice(frameEnd + 2);
      dispatchFrame(frame, handlers);
    }
  }
  // 마지막 잔여 buffer 도 시도
  if (buffer.trim()) {
    dispatchFrame(buffer, handlers);
  }
}

function dispatchFrame(frame: string, handlers: StreamHandlers): void {
  if (!frame.trim()) return;
  let eventName: StreamEventName | null = null;
  const dataLines: string[] = [];
  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim() as StreamEventName;
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }
  if (!eventName || dataLines.length === 0) return;

  let parsed: unknown;
  try {
    parsed = JSON.parse(dataLines.join('\n'));
  } catch {
    parsed = { raw: dataLines.join('\n') };
  }

  switch (eventName) {
    case 'tool_call':
      handlers.onToolCall?.(parsed as { name: string; args?: Record<string, unknown> });
      return;
    case 'tool_result':
      handlers.onToolResult?.(
        parsed as { name: string; latency_ms?: number; ok?: boolean; error?: string },
      );
      return;
    case 'navigate':
      handlers.onNavigate?.(parsed as { path: string; label?: string; domain?: string });
      return;
    case 'token':
      handlers.onToken?.(parsed as { text: string });
      return;
    case 'status':
      handlers.onStatus?.(parsed as { kind: 'thinking' | 'tool'; name?: string });
      return;
    case 'conversation':
      handlers.onConversation?.(parsed as { conversationId: string; isNew: boolean });
      return;
    case 'replace':
      handlers.onReplace?.(parsed as { text: string; reason?: string });
      return;
    case 'done':
      handlers.onDone?.(
        parsed as {
          responseTime?: string;
          botCreatedAt?: string;
          totalRounds?: number;
          meta?: Record<string, unknown>;
          charts?: Array<Record<string, unknown>>;
          alarmRecommendations?: Array<Record<string, unknown>>;
          structuredAnswer?: Record<string, unknown>;
        },
      );
      return;
    case 'error':
      handlers.onError?.(parsed as { message: string });
      return;
    default:
      // 알 수 없는 이벤트는 무시
      return;
  }
}
